import loadable from '@loadable/component';
import { BpkAccordion } from '@skyscanner-internal/backpack-web/bpk-component-accordion';
import {
  BpkBox,
  BpkBreakpoint,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import withErrorBoundary from '../ErrorBoundary/WithErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import AccordionItem from './AccordionItem';

import type { AccordionProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/AccordionProps';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const Accordion = ({ description, isFaq, items, title }: AccordionProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!items?.length) {
    return null;
  }

  return (
    <BpkBox
      data-testid="accordion"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      display="flex"
      flexDirection="column"
      gap={{
        base: BpkSpacing.Base,
        [BpkBreakpoint.SmallTablet]: BpkSpacing.LG,
      }}
      // Logical padding-end: right in LTR, left in RTL — replaces the SCSS
      // `padding-right` + `@include utils.bpk-rtl` override pair.
      paddingEnd={{
        base: BpkSpacing.None,
        [BpkBreakpoint.SmallTablet]: BpkSpacing.XL,
        [BpkBreakpoint.Tablet]: BpkSpacing.XXL,
      }}
    >
      <BpkSectionHeader title={title} description={description} />
      <BpkAccordion>
        {items.map((item) => (
          <AccordionItem key={item.id} isFaq={isFaq} {...item} />
        ))}
      </BpkAccordion>
    </BpkBox>
  );
};

const AccordionWrapper = (props: AccordionProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.ACCORDION,
      })
    }
  >
    <Accordion {...props} />
  </IntersectionObserverWrapper>
);
const AccordionWithErrorBoundary = withErrorBoundary(
  AccordionWrapper,
  'accordion',
);
export default AccordionWithErrorBoundary;
